import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Plus, Edit2, Trash2, Calendar, MapPin, Clock, FileText, FileSignature, Upload, Lock, Star, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import MinutesEditor from './MinutesEditor';
import { splitMeetings } from '../sortUtils';
import { API_BASE } from '../../../utils/apiBase';

const apiBase = API_BASE;
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const MEETING_TYPES = [
  { key: 'regular', label: 'Regular', color: '#6B3FA0' },
  { key: 'special', label: 'Special', color: '#FFB74D' },
  { key: 'annual', label: 'Annual', color: '#43A047' },
  { key: 'committee', label: 'Committee', color: '#3B82F6' },
];
const STATUS = [
  { key: 'scheduled', label: 'Scheduled', color: '#3B82F6' },
  { key: 'held', label: 'Held', color: '#43A047' },
  { key: 'cancelled', label: 'Cancelled', color: '#8E85A0' },
];

// 100 MB ceiling on agenda + minutes attachment uploads (matches backend)
const MEETING_UPLOAD_MAX = 100 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const empty = () => ({ title: '', meeting_date: '', time_start: '', time_end: '', location: '', meeting_type: 'regular', agenda: [], packet_doc_ids: [], attendee_member_ids: [], minutes: '', status: 'scheduled', agenda_file_pending: null, agenda_file_mime: '', agenda_file_name: '', agenda_file_size: 0, agenda_storage_path: '' });

export default function MeetingsTab({ api, isAdmin, members }) {
  const [list, setList] = useState([]);
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [minutesMeeting, setMinutesMeeting] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPast, setShowPast] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, d] = await Promise.all([api.get('/board/meetings'), api.get('/board/documents')]);
      setList(m.data); setDocs(d.data);
    } catch (e) { /* noop */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const memberName = (id) => members.find(m => m.id === id)?.name || '—';
  const docName = (id) => docs.find(d => d.id === id)?.name || '—';

  const openCreate = () => { setEditing(null); setForm({ ...empty(), meeting_date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...empty(), ...m, agenda_file_pending: null, agenda: m.agenda || [], packet_doc_ids: m.packet_doc_ids || [], attendee_member_ids: m.attendee_member_ids || [] }); setOpen(true); };

  const handleAgendaFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MEETING_UPLOAD_MAX) { toast.error(`Max ${MEETING_UPLOAD_MAX / 1024 / 1024} MB`); return; }
    // Stash the File object; we'll upload it via multipart after the meeting
    // is saved. This avoids the old base64-in-JSON path that capped at 8 MB.
    setForm(p => ({ ...p, agenda_file_pending: f, agenda_file_mime: f.type || 'application/pdf', agenda_file_name: f.name, agenda_file_size: f.size }));
    e.target.value = '';
  };
  const downloadAgendaFile = async (m) => {
    if (!m.id) return;
    try {
      const resp = await api.get(`/board/meetings/${m.id}/agenda`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: m.agenda_file_mime || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = m.agenda_file_name || 'agenda.pdf'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error('No agenda PDF attached'); }
  };
  const removeAgendaFile = () => setForm(p => ({ ...p, agenda_file_pending: null, agenda_file_name: '', agenda_file_mime: '', agenda_file_size: 0, agenda_storage_path: '' }));

  const save = async () => {
    if (!form.title.trim() || !form.meeting_date) { toast.error('Title and date required'); return; }
    if (editing && !editing.id) {
      toast.error('This meeting record is missing its ID. Please refresh the page and try again.');
      return;
    }
    setBusy(true);
    try {
      // Don't send the File object (or any pending blob) in the JSON body —
      // it'll be uploaded separately via multipart after save.
      const { agenda_file_pending, ...payload } = form;
      let meetingId = editing?.id;
      if (editing) {
        await api.put(`/board/meetings/${editing.id}`, payload);
      } else {
        const { data } = await api.post('/board/meetings', payload);
        meetingId = data.id;
      }
      // Upload the agenda PDF separately if the user picked a new file
      if (agenda_file_pending && meetingId) {
        const fd = new FormData();
        fd.append('file', agenda_file_pending);
        await api.post(`/board/meetings/${meetingId}/agenda`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
        });
      }
      toast.success('Saved'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const del = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    try { await api.delete(`/board/meetings/${m.id}`); toast.success('Deleted'); load(); } catch (e) { toast.error('Failed'); }
  };

  const addAgendaItem = () => setForm(f => ({ ...f, agenda: [...f.agenda, { title: '', presenter: '', duration_min: 10, notes: '' }] }));
  const updateAgenda = (i, field, value) => setForm(f => ({ ...f, agenda: f.agenda.map((a, j) => i === j ? { ...a, [field]: value } : a) }));
  const removeAgenda = (i) => setForm(f => ({ ...f, agenda: f.agenda.filter((_, j) => j !== i) }));
  const toggleDoc = (id) => setForm(f => ({ ...f, packet_doc_ids: f.packet_doc_ids.includes(id) ? f.packet_doc_ids.filter(x => x !== id) : [...f.packet_doc_ids, id] }));
  const toggleAttendee = (id) => setForm(f => ({ ...f, attendee_member_ids: f.attendee_member_ids.includes(id) ? f.attendee_member_ids.filter(x => x !== id) : [...f.attendee_member_ids, id] }));

  const printPacket = async (m) => {
    // Open an in-app packet viewer: agenda PDF + every linked document
    // embedded inline as iframes, plus a "Download as ZIP" button.
    const w = window.open('', '_blank');
    if (!w) { toast.error('Pop-up blocked. Please allow pop-ups to view the packet.'); return; }
    w.document.write(`<!doctype html><html><head><title>${escapeHtml(m.title)} — Board Packet</title>
      <style>
        body { font-family: Inter, Georgia, serif; max-width: 1100px; margin: 0 auto; padding: 24px; color: #2D1B4E; background: #F8F7FC; }
        header { border-bottom: 3px solid #6B3FA0; padding-bottom: 12px; margin-bottom: 18px; display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
        h1 { color: #6B3FA0; margin: 0 0 4px 0; }
        h2 { color: #2D1B4E; margin: 24px 0 8px; font-size: 16px; border-left: 4px solid #9B7ED8; padding-left: 8px; }
        .meta { color: #5A4D6E; font-size: 13px; line-height: 1.5; }
        .download { background: #6B3FA0; color: white; padding: 10px 16px; border-radius: 6px; text-decoration: none; font-weight: bold; font-size: 13px; }
        .download:hover { background: #5A2F8F; }
        iframe { width: 100%; height: 70vh; border: 1px solid #DED8EB; background: white; border-radius: 6px; }
        .doc { background: white; padding: 14px; border-radius: 8px; border: 1px solid #DED8EB; margin-bottom: 14px; box-shadow: 0 1px 2px rgba(45,27,78,0.05); }
        .doc-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; flex-wrap: wrap; gap: 6px; }
        .doc-name { font-weight: bold; }
        .doc-meta { color: #8E85A0; font-size: 12px; }
        .agenda-list { background: white; padding: 12px 18px; border-radius: 6px; border: 1px solid #DED8EB; }
        .empty { color: #8E85A0; font-style: italic; padding: 12px; background: white; border-radius: 6px; border: 1px dashed #DED8EB; }
        a.link { color: #6B3FA0; }
        button.dl-doc { background: #EDE7F6; color: #6B3FA0; border: 1px solid #9B7ED8; padding: 4px 10px; border-radius: 4px; font-size: 11px; font-weight: bold; cursor: pointer; }
        button.dl-doc:hover { background: #DED8EB; }
      </style></head><body>
      <header>
        <div>
          <h1>${escapeHtml(m.title)}</h1>
          <div class="meta">
            <strong>Date:</strong> ${escapeHtml(m.meeting_date || '—')} ${m.time_start ? `· ${escapeHtml(m.time_start)}${m.time_end ? `–${escapeHtml(m.time_end)}` : ''}` : ''}<br/>
            <strong>Location:</strong> ${escapeHtml(m.location || '—')}<br/>
            <strong>Type:</strong> ${escapeHtml(MEETING_TYPES.find(t => t.key === m.meeting_type)?.label || m.meeting_type || 'regular')}
          </div>
        </div>
        <a class="download" href="${apiBase}/api/board/meetings/${m.id}/packet.zip" download>⬇ Download Packet (ZIP)</a>
      </header>
      <div id="content"><p class="meta">Loading documents…</p></div>
    </body></html>`);
    w.document.close();

    // Fetch each linked doc's base64 in parallel
    let docs = [];
    try {
      const ids = m.packet_doc_ids || [];
      docs = await Promise.all(ids.map(id => api.get(`/board/documents/${id}`).then(r => r.data).catch(() => null)));
      docs = docs.filter(Boolean);
    } catch { /* tolerate fetch failures */ }

    const root = w.document.getElementById('content');
    if (!root) return;
    // Helper: fetch a file from the backend (with auth cookie) and return a
    // blob URL suitable for embedding in an iframe / img. Same-origin so the
    // popup window can read it. Caller is responsible for revoke (we leak
    // here on purpose — the popup may use it for the lifetime of the print).
    const fetchAsBlobUrl = async (url, mime) => {
      try {
        const resp = await api.get(url, { responseType: 'blob' });
        const blob = new Blob([resp.data], { type: mime || resp.data.type || 'application/octet-stream' });
        return URL.createObjectURL(blob);
      } catch {
        return '';
      }
    };

    let html = '';
    // Agenda items (structured)
    if ((m.agenda || []).length > 0) {
      html += '<h2>Agenda</h2><ol class="agenda-list">';
      for (const a of m.agenda) {
        html += `<li><strong>${escapeHtml(a.title)}</strong>${a.presenter ? ` — ${escapeHtml(a.presenter)}` : ''}${a.duration_min ? ` <span class="doc-meta">(${a.duration_min}m)</span>` : ''}${a.notes ? `<div class="doc-meta">${escapeHtml(a.notes)}</div>` : ''}</li>`;
      }
      html += '</ol>';
    }
    // Agenda PDF — fetched via streaming proxy (works for both storage and
    // legacy inline storage paths transparently)
    if (m.agenda_storage_path || m.agenda_file_name || m.agenda_file_b64) {
      const src = await fetchAsBlobUrl(`/board/meetings/${m.id}/agenda`, m.agenda_file_mime);
      if (src) {
        html += `<h2>Official Agenda — ${escapeHtml(m.agenda_file_name || 'Agenda.pdf')}</h2>
          <div class="doc"><iframe src="${src}"></iframe></div>`;
      }
    }
    // Linked docs — fetched via streaming proxy
    html += `<h2>Packet Documents ${docs.length ? `<span style="color:#8E85A0;font-weight:normal;font-size:12px">(${docs.length})</span>` : ''}</h2>`;
    if (!docs.length) {
      html += '<div class="empty">No supporting documents attached to this meeting.</div>';
    } else {
      for (const d of docs) {
        const src = await fetchAsBlobUrl(`/board/documents/${d.id}/download`, d.mime);
        const isPdf = (d.mime || '').includes('pdf') || (d.name || '').toLowerCase().endsWith('.pdf');
        const isImage = (d.mime || '').startsWith('image/');
        html += `<div class="doc">
          <div class="doc-head">
            <div><span class="doc-name">${escapeHtml(d.name || 'document')}</span> <span class="doc-meta">· ${escapeHtml(d.doc_type || '')}${d.version ? ` · v${escapeHtml(d.version)}` : ''}</span></div>
            <a class="link" href="${src}" download="${escapeHtml(d.name || 'document')}"><button class="dl-doc">Download</button></a>
          </div>
          ${src && isPdf ? `<iframe src="${src}"></iframe>` : src && isImage ? `<img src="${src}" style="max-width:100%;border-radius:6px"/>` : `<p class="doc-meta">Preview not available — click Download to open.</p>`}
        </div>`;
      }
    }
    root.innerHTML = html;
  };

  const extractAgendaFromPdf = async () => {
    let b64 = '';
    let mime = form.agenda_file_mime || 'application/pdf';
    if (form.agenda_file_pending) {
      // Encode the locally-picked File once for the AI call
      b64 = await fileToBase64(form.agenda_file_pending);
      mime = form.agenda_file_pending.type || mime;
    } else if (editing?.agenda_storage_path || editing?.agenda_file_name) {
      // Already uploaded — pull the bytes back via the download proxy
      try {
        const resp = await api.get(`/board/meetings/${editing.id}/agenda`, { responseType: 'blob' });
        b64 = await fileToBase64(resp.data);
      } catch {
        toast.error('Could not fetch the saved agenda PDF'); return;
      }
    } else {
      toast.error('Upload an agenda PDF first'); return;
    }
    setExtracting(true);
    try {
      const { data } = await api.post('/board/meetings/extract-agenda', {
        file_b64: b64,
        mime,
      });
      const items = data.items || [];
      if (items.length === 0) {
        toast.error('Could not detect agenda items in the PDF');
        return;
      }
      setForm(f => ({ ...f, agenda: [...f.agenda, ...items] }));
      toast.success(`Added ${items.length} agenda item${items.length === 1 ? '' : 's'} from PDF`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  const filterFn = (m) => (typeFilter === 'all' || m.meeting_type === typeFilter) && (statusFilter === 'all' || m.status === statusFilter);
  const { upcoming, past } = React.useMemo(() => {
    const filtered = (list || []).filter(filterFn);
    return splitMeetings(filtered);
  }, [list, typeFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const nextMeetingId = upcoming[0]?.id;

  const renderMeetingCard = (m) => {
    const type = MEETING_TYPES.find(t => t.key === m.meeting_type) || MEETING_TYPES[0];
    const stat = STATUS.find(s => s.key === m.status) || STATUS[0];
    const isOpen = expanded === m.id;
    const isNext = m.id === nextMeetingId;
    return (
      <Card key={m.id}
        className={`bg-white ${isNext ? 'border-[#6B3FA0] border-2 shadow-sm' : 'border-[#DED8EB]'}`}
        data-testid={`meeting-${m.id}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(isOpen ? null : m.id)}>
              {isNext ? <Star className="h-4 w-4 text-[#FFB74D] fill-[#FFB74D] shrink-0" /> : <Calendar className="h-4 w-4 text-[#6B3FA0] shrink-0" />}
              <div>
                <p className="font-bold text-[#2D1B4E]">
                  {m.title}
                  {isNext && <Badge className="ml-2 text-[10px] bg-[#EDE7F6] text-[#6B3FA0] border-[#9B7ED8]">NEXT</Badge>}
                </p>
                <p className="text-xs text-[#5A4D6E]">
                  {m.meeting_date} {m.time_start && `· ${m.time_start}`} {m.location && `· ${m.location}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Badge style={{ background: `${type.color}22`, color: type.color, borderColor: type.color }}>{type.label}</Badge>
              <Badge style={{ background: `${stat.color}22`, color: stat.color }}>{stat.label}</Badge>
              {m.minutes_status === 'approved' && <Badge className="text-[10px] bg-[#E8F5E9] text-[#1B5E20] border border-[#43A047]"><Lock className="h-2.5 w-2.5 inline mr-0.5" /> Minutes Locked</Badge>}
              {m.minutes_status === 'pending_approval' && <Badge className="text-[10px] bg-[#FFF3E0] text-[#E65100] border border-[#FFB74D]">Minutes Pending</Badge>}
              <Button variant="outline" size="sm" onClick={() => setMinutesMeeting(m)} className="h-7 text-xs" data-testid={`minutes-btn-${m.id}`}><FileSignature className="h-3 w-3 mr-1" /> Minutes</Button>
              <Button variant="ghost" size="sm" onClick={() => printPacket(m)} className="h-7 text-xs" data-testid={`print-packet-${m.id}`}><FileText className="h-3 w-3 mr-1" /> Packet</Button>
              {isAdmin && (
                <>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(m)} className="h-7 w-7"><Edit2 className="h-3 w-3" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(m)} className="h-7 w-7 text-[#D9534F]"><Trash2 className="h-3 w-3" /></Button>
                </>
              )}
            </div>
          </div>
          {isOpen && (
            <div className="mt-3 pt-3 border-t border-[#F0EDF6] space-y-2 text-sm" data-testid={`meeting-detail-${m.id}`}>
              {(m.agenda_storage_path || m.agenda_file_name || m.agenda_file_b64) && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#5A4D6E]">Official Agenda</p>
                  <button onClick={() => downloadAgendaFile(m)} className="text-xs text-[#6B3FA0] underline mt-1 flex items-center gap-1" data-testid={`download-agenda-${m.id}`}>
                    <FileText className="h-3 w-3" /> {m.agenda_file_name || 'Download agenda PDF'}
                  </button>
                </div>
              )}
              {(m.agenda || []).length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#5A4D6E]">Agenda</p>
                  <ol className="list-decimal pl-5 mt-1 space-y-0.5 text-xs text-[#2D1B4E]">
                    {(m.agenda || []).map((a, i) => <li key={i}>{a.title}{a.presenter && ` — ${a.presenter}`}{a.duration_min ? ` (${a.duration_min}m)` : ''}</li>)}
                  </ol>
                </div>
              )}
              {(m.packet_doc_ids || []).length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#5A4D6E]">Documents</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {m.packet_doc_ids.map(id => <Badge key={id} className="text-[10px] bg-[#F0EDF6] text-[#6B3FA0]">{docName(id)}</Badge>)}
                  </div>
                </div>
              )}
              {(m.attendee_member_ids || []).length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#5A4D6E]">Expected Attendees</p>
                  <p className="text-xs text-[#5A4D6E] mt-1">{m.attendee_member_ids.map(memberName).join(', ')}</p>
                </div>
              )}
              {m.minutes && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#5A4D6E]">Minutes</p>
                  <pre className="text-xs whitespace-pre-wrap text-[#2D1B4E] bg-[#F7F4EF] p-2 rounded mt-1">{m.minutes}</pre>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="meetings-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-[#2D1B4E]">Board Meetings</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="border border-[#DED8EB] rounded px-2 py-1.5 text-xs bg-white" data-testid="meeting-type-filter">
            <option value="all">All types</option>
            {MEETING_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="border border-[#DED8EB] rounded px-2 py-1.5 text-xs bg-white" data-testid="meeting-status-filter">
            <option value="all">All statuses</option>
            {STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {isAdmin && <Button onClick={openCreate} className="bg-[#6B3FA0]" data-testid="add-meeting-btn"><Plus className="h-4 w-4 mr-1" /> Schedule Meeting</Button>}
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-center py-12 text-[#8E85A0] text-sm">No meetings scheduled yet.</p>
      ) : (
        <>
          <div className="space-y-2" data-testid="meetings-upcoming-section">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#5A4D6E] flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Upcoming
                <Badge className="bg-[#EDE7F6] text-[#6B3FA0] border-[#9B7ED8] text-[10px]">{upcoming.length}</Badge>
              </h3>
            </div>
            {upcoming.length === 0 ? (
              <p className="text-xs text-[#8E85A0] italic px-1">No upcoming meetings.</p>
            ) : (
              upcoming.map(renderMeetingCard)
            )}
          </div>

          <div className="space-y-2 mt-6" data-testid="meetings-past-section">
            <button
              onClick={() => setShowPast(s => !s)}
              className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#5A4D6E] hover:text-[#2D1B4E]"
              data-testid="toggle-past-meetings">
              <Clock className="h-3.5 w-3.5" />
              Past Meetings
              <Badge className="bg-[#F0EDF6] text-[#8E85A0] border-[#DED8EB] text-[10px]">{past.length}</Badge>
              <span className="text-[10px] font-normal lowercase tracking-normal">({showPast ? 'hide' : 'show'})</span>
            </button>
            {showPast && (
              past.length === 0
                ? <p className="text-xs text-[#8E85A0] italic px-1">No past meetings.</p>
                : past.map(renderMeetingCard)
            )}
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Schedule'} Board Meeting</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Q1 Board Meeting" data-testid="mt-title" /></div>
              <div><Label className="text-xs">Date *</Label><Input type="date" value={form.meeting_date} onChange={e => setForm(f => ({ ...f, meeting_date: e.target.value }))} data-testid="mt-date" /></div>
              <div><Label className="text-xs">Type</Label>
                <select value={form.meeting_type} onChange={e => setForm(f => ({ ...f, meeting_type: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white">
                  {MEETING_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Start Time</Label><Input type="time" value={form.time_start} onChange={e => setForm(f => ({ ...f, time_start: e.target.value }))} /></div>
              <div><Label className="text-xs">End Time</Label><Input type="time" value={form.time_end} onChange={e => setForm(f => ({ ...f, time_end: e.target.value }))} /></div>
              <div className="col-span-2"><Label className="text-xs">Location</Label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Conference room / Zoom link" /></div>
              <div><Label className="text-xs">Status</Label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white">
                  {STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {/* Agenda */}
            <div>
              <div className="flex items-center justify-between flex-wrap gap-1">
                <Label className="text-xs">Agenda Items</Label>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={extractAgendaFromPdf}
                    disabled={!(form.agenda_file_pending || form.agenda_storage_path || form.agenda_file_name) || extracting}
                    className="h-7 text-xs border-[#9B7ED8] text-[#6B3FA0] hover:bg-[#EDE7F6] disabled:opacity-50"
                    title={(form.agenda_file_pending || form.agenda_storage_path || form.agenda_file_name) ? 'Use AI to parse the uploaded agenda PDF into items below' : 'Upload an agenda PDF below first'}
                    data-testid="autofill-agenda-btn">
                    <Wand2 className="h-3 w-3 mr-1" /> {extracting ? 'Reading PDF…' : 'Auto-fill from PDF'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={addAgendaItem} className="h-7 text-xs"><Plus className="h-3 w-3 mr-1" /> Add item</Button>
                </div>
              </div>
              <div className="space-y-2 mt-1">
                {(form.agenda || []).map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`agenda-row-${i}`}>
                    <Input value={a.title} onChange={e => updateAgenda(i, 'title', e.target.value)} placeholder="Item" className="col-span-5 h-8 text-xs" />
                    <Input value={a.presenter} onChange={e => updateAgenda(i, 'presenter', e.target.value)} placeholder="Presenter" className="col-span-3 h-8 text-xs" />
                    <Input type="number" value={a.duration_min} onChange={e => updateAgenda(i, 'duration_min', parseInt(e.target.value) || 0)} placeholder="min" className="col-span-2 h-8 text-xs" />
                    <Input value={a.notes} onChange={e => updateAgenda(i, 'notes', e.target.value)} placeholder="Notes" className="col-span-1 h-8 text-xs" />
                    <Button variant="ghost" size="icon" onClick={() => removeAgenda(i)} className="h-7 w-7 text-[#D9534F] col-span-1"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Official Agenda File (PDF/DOC) */}
            <div>
              <Label className="text-xs">Official Agenda File (optional, ≤100MB)</Label>
              {(form.agenda_file_pending || form.agenda_storage_path || form.agenda_file_name) ? (
                <div className="flex items-center justify-between bg-[#F7F4EF] border border-[#DED8EB] rounded px-2 py-1.5 mt-1 text-xs">
                  <span className="flex items-center gap-1.5 truncate">
                    <FileText className="h-3 w-3 text-[#6B3FA0]" /> {form.agenda_file_name || 'agenda'}
                    {form.agenda_file_size > 0 && <span className="text-[10px] text-[#8E85A0]">({form.agenda_file_size >= 1024 * 1024 ? (form.agenda_file_size / 1024 / 1024).toFixed(1) + 'MB' : (form.agenda_file_size / 1024).toFixed(0) + 'KB'})</span>}
                    {form.agenda_file_pending && <span className="text-[10px] text-[#6B3FA0] font-semibold">(uploads on save)</span>}
                  </span>
                  <button onClick={removeAgendaFile} className="text-[#D9534F]" data-testid="remove-agenda-file"><Trash2 className="h-3 w-3" /></button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-1.5 border-2 border-dashed border-[#DED8EB] rounded p-2 cursor-pointer hover:border-[#9B7ED8] text-xs text-[#5A4D6E] mt-1" data-testid="agenda-file-drop">
                  <Upload className="h-3 w-3 text-[#6B3FA0]" /> Attach signed/formatted agenda PDF
                  <input type="file" onChange={handleAgendaFile} className="hidden" accept=".pdf,.doc,.docx" data-testid="agenda-file-input" />
                </label>
              )}
            </div>

            {/* Doc packet */}
            {docs.length > 0 && (
              <div>
                <Label className="text-xs">Packet Documents</Label>
                <div className="max-h-32 overflow-y-auto border border-[#DED8EB] rounded p-2 mt-1 space-y-1">
                  {docs.map(d => (
                    <label key={d.id} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form.packet_doc_ids.includes(d.id)} onChange={() => toggleDoc(d.id)} />
                      {d.name} <span className="text-[#8E85A0]">· {d.doc_type}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Attendees */}
            <div>
              <Label className="text-xs">Expected Attendees</Label>
              <div className="max-h-32 overflow-y-auto border border-[#DED8EB] rounded p-2 mt-1 space-y-1">
                {members.map(m => (
                  <label key={m.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.attendee_member_ids.includes(m.id)} onChange={() => toggleAttendee(m.id)} />
                    {m.name}
                  </label>
                ))}
              </div>
            </div>

            <div><Label className="text-xs">Quick Notes (use the dedicated Minutes editor for full minutes workflow)</Label>
              <textarea value={form.minutes} onChange={e => setForm(f => ({ ...f, minutes: e.target.value }))} rows={2} className="w-full border border-[#DED8EB] rounded px-2 py-1 text-sm font-mono" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#6B3FA0]" data-testid="save-meeting-btn">{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <MinutesEditor
        open={!!minutesMeeting}
        onClose={() => setMinutesMeeting(null)}
        api={api}
        meeting={minutesMeeting}
        members={members}
        isAdmin={isAdmin}
        onSaved={async () => {
          // Re-fetch the list AND re-hydrate the currently-open meeting so
          // newly-added decisions / status changes appear without reopen.
          try {
            const { data } = await api.get('/board/meetings');
            setList(data);
            if (minutesMeeting) {
              const fresh = data.find(x => x.id === minutesMeeting.id);
              if (fresh) setMinutesMeeting(fresh);
            }
          } catch (e) { /* noop */ }
        }}
      />
    </div>
  );
}
